import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '60 minutes',
  session: '1 session',
  results: 'Results 6-8 weeks',
  cost: 'From £45',
  downtime: 'None',
}

const relatedTreatments = [
  {
    name: 'Brow Lamination & Tint Cheshunt',
    slug: 'eyebrows-lamination-and-tint',
    description:
      'Match your lifted lashes with perfectly groomed, fuller-looking brows.',
  },
  {
    name: 'Microblading & Nanoblading Cheshunt',
    slug: 'microblading',
    description:
      'Consider semi-permanent brows to pair with your low-maintenance lash routine.',
  },
  {
    name: 'Dermaplaning Facial Cheshunt',
    slug: 'dermaplaning',
    description:
      'Create a smooth, glowing complexion to complement your eye-area enhancements.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much does lash lift and tint cost in Cheshunt?',
    answer:
      'Lash lift and tint treatments at Ada Aesthetics in Cheshunt cost £45. This includes the full lash lifting treatment with a professional tint for darker, more defined lashes that last 6-8 weeks. Book a free consultation at our Cheshunt clinic to discuss your lash goals.',
  },
  {
    question: 'Will it hurt?',
    answer:
      'A lash lift is not painful, but your eyes might water a little. First of all, a silicone pad is placed on your closed eyelids. Your lashes are then curled over the top, and a lifting lotion is applied, followed by a setting lotion to set the lift. The treatment finishes with a lash tint for extra definition. Lasts 6-8 weeks.',
  },
  {
    question: 'How does it work?',
    answer:
      'Think of a lash lift as a perm for your lashes: it does not add length like extensions, but the curl makes your lashes look longer. Most lashes grow out, not up, so curling upwards opens up your eyes for a wide-eyed look. After tinting, your lashes will look thicker, darker and longer-looking. However, if you have short stubby lashes, stick with extensions.',
  },
  {
    question: 'How long does a lash lift last?',
    answer:
      'A lash lift at Ada Aesthetics in Cheshunt typically lasts 6-8 weeks, depending on your natural lash growth cycle. We recommend avoiding water and steam for the first 24 hours after treatment to ensure the lift sets properly for maximum longevity.',
  },
  {
    question: 'Lash lift vs lash extensions - which is better?',
    answer:
      'Lash lifts and extensions serve different purposes. At our Cheshunt clinic, we recommend lash lifts for those who want a natural, low-maintenance look with their own lashes. Extensions add dramatic length and volume but require more upkeep and can damage natural lashes. A lash lift is perfect if you already have decent length lashes.',
  },
]

const LashLift: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Lash Lift and Tint in Cheshunt',
    'Professional lash lift and tint treatments in Cheshunt, Hertfordshire. Curl and lift your natural lashes for a wide-eyed, glamorous look. Low-maintenance alternative to lash extensions.',
    '45'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Lash Lift and Tint Cheshunt', url: 'https://adaaesthetics.uk/lash-lift-and-tint' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Lash Lift & Tint Cheshunt - Natural Lash Enhancement | Ada Aesthetics"
        description="Professional lash lift and tint in Cheshunt for £45. Curl and lift your natural lashes for 6-8 weeks. Low-maintenance alternative to extensions. Book: 07478705252"
        keywords="lash lift Cheshunt, lash tint, eyelash lift Hertfordshire, lash perm Cheshunt, natural lash enhancement"
        url="https://adaaesthetics.uk/lash-lift-and-tint"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Lash Lift and Tint" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Wake Up to Beautifully Curled Lashes Every Day with Lash Lift & Tint in Cheshunt</h1>
              <p className="excerpt">
                Tired of curling your lashes every morning or dealing with high-maintenance extensions? A lash lift and
                tint at Ada Aesthetics in Cheshunt gives you darker, beautifully curled lashes that last 6–8 weeks—no
                daily effort required. In just 60 minutes, you can wake up every day with wide-eyed, mascara-optional
                lashes. Just £45. Serving Cheshunt, Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>

              <h2>What is a Lash Lift?</h2>
              <p>
                Are you looking for a low-maintenance alternative to lash
                extensions? Fed up with the damage and upkeep? If you
                want a more natural look, a lash lift will curl and lift your
                lashes for 6-8 weeks. Think of a lash lift as a perm for your lashes: it does not add length like extensions, but the curl makes your lashes look longer. Most lashes grow out, not up, so curling upwards opens up your eyes for a wide-eyed look.
              </p>
              <p>
                At Ada Aesthetics in Cheshunt, we understand the importance of
                well-defined lashes that enhance your eyes. We specialise in eyelash
                treatments and believe that eyelash care is an essential part of your
                grooming routine, just as important as skincare and haircare.
              </p>

              <h2>How Does Lash Lift Work?</h2>
              <p>
                A lash lift is not painful, but your eyes might water a little. First of all, a silicone pad is placed on your closed eyelids. Your lashes are then curled over the top, and a lifting lotion is applied, followed by a setting lotion to set the lift. The treatment finishes with a lash tint for extra definition. After tinting, your lashes will look thicker, darker and longer-looking.
              </p>
              <p>
                However, if you have short stubby lashes, stick with extensions. A lash lift works best for clients who already have decent length lashes.
              </p>

              <h2>Lash Lift vs Lash Extensions</h2>
              <p>
                Lash lifts and extensions serve different purposes. At our Cheshunt clinic, we recommend lash lifts for those who want a natural, low-maintenance look with their own lashes. Extensions add dramatic length and volume but require more upkeep and can damage natural lashes. A lash lift is perfect if you already have decent length lashes and want a natural enhancement.
              </p>

              <h2>Benefits of Lash Lift & Tint</h2>
              <ul>
                <li><strong>Low-maintenance</strong> - no daily curling or mascara needed</li>
                <li><strong>Natural-looking results</strong> - enhances your own lashes</li>
                <li><strong>Long-lasting</strong> - results last 6-8 weeks</li>
                <li><strong>Wide-eyed appearance</strong> - opens up your eyes</li>
                <li><strong>No damage</strong> - unlike extensions, doesn't harm natural lashes</li>
                <li><strong>Affordable</strong> - just £45 for lasting results</li>
                <li><strong>Quick treatment</strong> - 60 minutes for beautiful lashes</li>
              </ul>

              <h2>Why Choose Ada Aesthetics for Lash Lift in Cheshunt?</h2>
              <ul>
                <li><strong>Expert practitioners</strong> trained in lash lifting techniques</li>
                <li><strong>Professional products</strong> - safe and effective</li>
                <li><strong>Natural results</strong> - enhances your own lashes beautifully</li>
                <li><strong>Competitive pricing</strong> - just £45</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
              </ul>

              <h2>Book Your Lash Lift & Tint in Cheshunt</h2>
              <p>
                Ready for beautifully curled, defined lashes that last? Join clients who trust us for professional lash lift treatments.
                Schedule your appointment today - our experienced team will help you achieve the perfect wide-eyed look.
              </p>
              <p>
                <strong>Book today to secure your appointment</strong> - Call us at <strong>07478705252</strong> or reserve your treatment online.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Lash Lift Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>
          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default LashLift

export const pageQuery = graphql`
  query LashLift {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
